/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA CÉDULA DOMINICANA — QUE NO PASE CUALQUIER COSA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── POR QUÉ EXISTE ──────────────────────────────────────────────────────
 *
 * El 4-oct-2026 el fundador probó el registro de negocio y reportó: «las
 * casillas se pueden llenar con datos a lo loco y sigue avanzando». Tenía
 * razón. La validación entera de la cédula era ésta:
 *
 *     const cedulaDigits = String(formData.cedula).replace(/\D/g, '');
 *     if (cedulaDigits.length < 10) { ...rechazar... }
 *
 * O sea: diez dígitos cualesquiera. Pasaban `00000000000`, `12345678901`,
 * `11111111111` y hasta `aaa1234567890` (las letras se caen en el replace).
 * Y esa cédula es lo que dice de quién es el negocio.
 *
 * Es el mismo error exacto que motivó `src/utils/placa.ts` («cuatro caracteres
 * cualesquiera»). Por eso este archivo se parece a aquél.
 *
 * ── LAS TRES PUERTAS, Y POR QUÉ ESTÁN EN ESE ORDEN ──────────────────────
 *
 * 1. ONCE DÍGITOS EXACTOS. Ni diez ni doce. Es lo mismo que ya exige el KYC
 *    en el servidor (`huellaDeCedula`, functions/src/kyc.ts: `if
 *    (digitos.length !== 11) return null`), así que las dos puertas de la app
 *    piden lo mismo. Si aquí se pidieran diez, alguien pasaría el formulario y
 *    luego no podría verificarse — que es peor que no dejarlo entrar.
 *
 * 2. NADA DE RELLENO. Once ceros, once unos, o `12345678901`. No son cédulas:
 *    son lo que escribe quien quiere salir del paso. Cuestan una línea de
 *    comprobación y son el 90% de lo que el fundador vio.
 *
 * 3. EL DÍGITO VERIFICADOR. Los diez primeros dígitos pesados alternando 1 y
 *    2; cuando el producto pasa de 9 se le restan 9 (que es lo mismo que sumar
 *    sus cifras); el total más el undécimo dígito tiene que ser múltiplo de
 *    10. Es Luhn, y es el algoritmo que usa la JCE.
 *
 * ── LO QUE ESTO CUESTA, DICHO DE FRENTE ─────────────────────────────────
 *
 * El dígito verificador tumba ~9 de cada 10 números inventados al azar. Pero
 * si algún día un dueño de negocio con una cédula REAL se queja de que no lo
 * deja pasar, la salida es quitar SOLO la puerta 3 (`CON_DIGITO_VERIFICADOR =
 * false` abajo) y dejar las otras dos: se sigue frenando el relleno y se deja
 * de frenar a nadie de verdad. Está puesto como un interruptor a propósito
 * para que esa decisión sea una línea y no una cirugía.
 *
 * Y la cédula NO es la única defensa: un admin mira la foto del local y los
 * documentos antes de aprobar. Esto es el filtro de la basura, no la prueba de
 * la identidad.
 */

/**
 * Interruptor de la tercera puerta. Ponerlo en `false` deja pasar cualquier
 * combinación de 11 dígitos que no sea relleno obvio — ver la nota de arriba.
 */
const CON_DIGITO_VERIFICADOR = true;

/** Patrones que no son una cédula, son alguien saliendo del paso. */
const RELLENOS = new Set([
  '00000000000',
  '11111111111',
  '22222222222',
  '33333333333',
  '44444444444',
  '55555555555',
  '66666666666',
  '77777777777',
  '88888888888',
  '99999999999',
  '12345678901',
  '01234567890',
  '98765432109',
]);

export type ResultadoDeCedula =
  | { ok: true; digitos: string; formateada: string }
  | { ok: false; motivo: string };

/** Solo los dígitos, sin guiones ni espacios. */
export function soloDigitosDeCedula(valor: unknown): string {
  return String(valor ?? '').replace(/\D/g, '');
}

/** `00112345673` → `001-1234567-3`, que es como se lee en el documento. */
export function formatearCedula(digitos: string): string {
  if (digitos.length !== 11) return digitos;
  return `${digitos.slice(0, 3)}-${digitos.slice(3, 10)}-${digitos.slice(10)}`;
}

/**
 * El dígito verificador (Luhn). Devuelve `true` si el número cuadra.
 *
 * Se exporta aparte para poder probarlo solo, sin las otras dos puertas.
 */
export function digitoVerificadorCuadra(digitos: string): boolean {
  if (digitos.length !== 11) return false;
  let suma = 0;
  for (let i = 0; i < 10; i++) {
    const peso = i % 2 === 0 ? 1 : 2;
    let producto = Number(digitos[i]) * peso;
    // Un producto de dos cifras cuenta como la suma de sus cifras: 14 → 5.
    // Restar 9 es lo mismo y no hace falta convertir a texto.
    if (producto > 9) producto -= 9;
    suma += producto;
  }
  const esperado = (10 - (suma % 10)) % 10;
  return esperado === Number(digitos[10]);
}

/**
 * La comprobación completa. Devuelve el motivo EN ESPAÑOL y dirigido a la
 * persona, porque este texto se le enseña tal cual: un «formato inválido» no
 * le dice a nadie qué tiene que corregir.
 */
export function validarCedula(valor: unknown): ResultadoDeCedula {
  const digitos = soloDigitosDeCedula(valor);

  if (digitos.length === 0) {
    return { ok: false, motivo: 'Escribe el número de cédula del dueño del negocio.' };
  }
  if (digitos.length !== 11) {
    return {
      ok: false,
      motivo:
        `La cédula tiene 11 números y escribiste ${digitos.length}. ` +
        'Cópiala tal como aparece en el documento, por ejemplo 001-1234567-3.',
    };
  }
  if (RELLENOS.has(digitos)) {
    return {
      ok: false,
      motivo: 'Ese número no es una cédula. Escribe la cédula real del dueño del negocio.',
    };
  }
  if (CON_DIGITO_VERIFICADOR && !digitoVerificadorCuadra(digitos)) {
    return {
      ok: false,
      motivo:
        'Ese número de cédula no es válido. Revísalo con calma: suele ser un dígito ' +
        'cambiado o uno de más. Debe quedar como 001-1234567-3.',
    };
  }
  return { ok: true, digitos, formateada: formatearCedula(digitos) };
}
